<template>
  <div class="homepage-container homepage-v2">
    <!-- Section 1: Promo Header Section -->
    <section class="promo-header text-center">
      <div class="container">
        <h1 class="promo-title animate-fade-in" v-html="$t('Home.V2BannerTitle')"></h1>
        <p class="promo-desc animate-fade-in delay-1" v-html="$t('Home.V2BannerDesc')"></p>
      </div>
    </section>

    <!-- Section 2: Claim Your Offer Section -->
    <section class="claim-offer">
      <!-- Desktop side triangles absolute positioned at the edges -->
      <div class="triangles-side left-side d-none d-lg-flex animate-slide-left">
        <img :src="$t('Home.ClaimYourOfferDesktopImg')" alt="Decorative Pattern" />
      </div>
      
      <div class="container">
        <div class="claim-content text-center animate-fade-in delay-2">
          <!-- Mobile view decorative triangles pattern placed above the header -->
          <div class="mobile-triangles-wrapper d-block d-lg-none">
            <img :src="$t('Home.ClaimYourOfferMobileImg')" alt="Decorative Pattern" class="mobile-triangles" />
          </div>

          <h2 class="claim-heading">{{ $t('Home.ReadyToSubmit') }}</h2>
          <p class="claim-text" v-html="$t('Home.ReadyToSubmitDesc')"></p>
          
          <div class="btn-container">
            <router-link to="/SignIn" class="btn btn-claim-offer">
              {{ $t('Home.GetStartedBtn') }}
            </router-link>
          </div>

          <div class="claim-nav-links">
            <div class="nav-link-item">
              <a href="#" class="accent-link d-inline-block mb-2">{{ $t('Header.ClaimOfferTab') }}</a>
            </div>
            <div class="nav-link-item">
              {{ $t('Home.TroubleParticipating') }} <router-link to="/FAQ" class="accent-link">{{ $t('Home.FAQsText') }}</router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="triangles-side right-side d-none d-lg-flex animate-slide-right">
        <img :src="$t('Home.ClaimYourOfferDesktopImg')" alt="Decorative Pattern" class="flip-horizontal" />
      </div>
    </section>

    <!-- Section 3: How to Participate Section -->
    <section class="how-it-works" :style="{ '--htw-bg-desktop': 'url(' + $t('Home.HtwBgDesktop') + ')', '--htw-bg-mobile': 'url(' + $t('Home.HtwBgMobile') + ')' }">
      <div class="container htw-container">
        <h2 class="htw-heading text-center animate-fade-in">{{ $t('Home.HtwTitleDesktop') }}</h2>
        
        <div class="row htw-cards-grid justify-content-center">
          <!-- Card 1 -->
          <div class="col-lg-3 col-md-6 mb-4 animate-card-delay-1">
            <div class="htw-card">
              <div class="card-icon-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2584C5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-shopping-cart"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
              </div>
              <h3 class="card-title">{{ $t('Home.HtwV2Step1Title') }}</h3>
              <p class="card-desc">{{ $t('Home.HtwV2Step1Desc') }}</p>
            </div>
          </div>

          <!-- Card 2 -->
          <div class="col-lg-3 col-md-6 mb-4 animate-card-delay-2">
            <div class="htw-card">
              <div class="card-icon-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2584C5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-camera"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
              </div>
              <h3 class="card-title">{{ $t('Home.HtwV2Step2Title') }}</h3>
              <p class="card-desc">{{ $t('Home.HtwV2Step2Desc') }}</p>
            </div>
          </div>

          <!-- Card 3 -->
          <div class="col-lg-3 col-md-6 mb-4 animate-card-delay-3">
            <div class="htw-card">
              <div class="card-icon-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2584C5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-upload"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
              </div>
              <h3 class="card-title">{{ $t('Home.HtwV2Step3Title') }}</h3>
              <p class="card-desc">{{ $t('Home.HtwV2Step3Desc') }}</p>
            </div>
          </div>

          <!-- Card 4 -->
          <div class="col-lg-3 col-md-6 mb-4 animate-card-delay-4">
            <div class="htw-card">
              <div class="card-icon-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2584C5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-shield"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><polyline points="9 11 11 13 15 9"></polyline></svg>
              </div>
              <h3 class="card-title">{{ $t('Home.HtwV2Step4Title') }}</h3>
              <p class="card-desc">{{ $t('Home.HtwV2Step4Desc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomePageV2',
  props: {
    startDate: {
      type: Date,
      required: true,
    },
    endDate: {
      type: Date,
      required: true,
    },
  },
};
</script>

<style scoped>
/* Promo Header Section */
.promo-header {
  padding: 3rem 0;
  background-color: #ffffff;
}

.promo-title {
  color: #2584C5;
  font-size: 2.5rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
}

:deep(.text-primary) {
  color: #2584C5 !important;
}

:deep(.text-dark) {
  color: #000000 !important;
}

.promo-desc {
  font-size: 1.15rem;
  color: #333333;
  line-height: 1.7;
  max-width: 860px;
  margin: 0 auto;
}

/* Claim Your Offer Section */
.claim-offer {
  padding: 3.5rem 0;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
}

.triangles-side {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  max-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.left-side {
  left: 0;
}

.right-side {
  right: 0;
}

.flip-horizontal {
  transform: scaleX(-1);
}

.claim-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
}

.mobile-triangles-wrapper {
  margin-bottom: 2rem;
  width: 100%;
}

.mobile-triangles {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
}

.claim-heading {
  font-size: 2.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1rem;
}

.claim-text {
  font-size: 1.1rem;
  color: #555555;
  margin-bottom: 2rem;
}

.btn-container {
  margin-bottom: 2.25rem;
}

.btn-claim-offer {
  display: inline-block;
  background-color: #2584C5;
  color: #ffffff !important;
  font-size: 1.15rem;
  font-weight: 600;
  padding: 0.75rem 5rem;
  border-radius: 4px;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(37, 132, 197, 0.25);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: none;
}

.btn-claim-offer:hover {
  background-color: #1b6ca2;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(37, 132, 197, 0.4);
  text-decoration: none !important;
}

.btn-claim-offer:active {
  transform: translateY(0);
}

.claim-nav-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nav-link-item {
  font-size: 0.95rem;
  color: #666666;
}

.accent-link {
  color: #2584C5 !important;
  text-decoration: underline !important;
  font-weight: 500;
}

.accent-link:hover {
  color: #1b6ca2 !important;
}

/* How It Works Section */
.how-it-works {
  background-image: var(--htw-bg-desktop);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  padding: 6rem 0;
  position: relative;
}

.htw-container {
  max-width: 1200px;
  margin: 0 auto;
}

.htw-heading {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 4rem;
}

.htw-cards-grid {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
}

.htw-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 2.5rem 1.5rem;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  height: 100%;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.htw-card:hover {
  transform: translateY(-8px);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  background: rgba(255, 255, 255, 0.12);
}

.card-icon-container {
  background: #ffffff;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.card-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
}

.card-desc {
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0;
}

/* SEO Copy Styles */
.seo-headline {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  max-width: 800px;
  margin: 0 auto;
}

.seo-desc {
  font-size: 1rem;
  color: #4b5563;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
}

.abbrv-terms {
  font-size: 0.8rem;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Animations */
.animate-fade-in {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
}

.delay-1 {
  animation-delay: 0.2s;
}

.delay-2 {
  animation-delay: 0.4s;
}

.animate-slide-left {
  opacity: 0;
  transform: translateY(-50%) translateX(-30px);
  animation: slideLeft 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.3s;
}

.animate-slide-right {
  opacity: 0;
  transform: translateY(-50%) translateX(30px);
  animation: slideRight 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.3s;
}

.animate-card-delay-1 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.4s;
}

.animate-card-delay-2 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.6s;
}

.animate-card-delay-3 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.8s;
}

.animate-card-delay-4 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 1s;
}

@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeft {
  to {
    opacity: 0.85;
    transform: translateY(-50%) translateX(0);
  }
}

@keyframes slideRight {
  to {
    opacity: 0.85;
    transform: translateY(-50%) translateX(0);
  }
}

/* Responsive Design */
@media screen and (max-width: 1199px) {
  .promo-title {
    font-size: 2.25rem;
  }
  .claim-heading {
    font-size: 2rem;
  }
  .htw-heading {
    font-size: 2.25rem;
  }
  .triangles-side {
    width: 15%;
  }
  .htw-card {
    padding: 2rem 1.25rem;
  }
}

@media screen and (max-width: 991px) {
  .how-it-works {
    padding: 4.5rem 0;
  }
  
  .htw-heading {
    margin-bottom: 3rem;
  }
}

@media screen and (max-width: 767px) {
  .promo-title {
    font-size: 1.85rem;
    margin-top: 1rem;
  }
  
  .promo-desc {
    font-size: 1rem;
    padding: 0 10px;
  }
  
  .claim-offer {
    padding: 2.5rem 0;
  }
  
  .claim-heading {
    font-size: 1.75rem;
  }
  
  .claim-text {
    font-size: 1rem;
  }
  
  .btn-claim-offer {
    padding: 0.75rem 3.5rem;
    font-size: 1.05rem;
    width: 100%;
    max-width: 280px;
  }
  
  .how-it-works {
    background-image: var(--htw-bg-mobile);
    padding: 3.5rem 0;
  }
  
  .htw-heading {
    font-size: 1.85rem;
    margin-bottom: 2.5rem;
  }
  
  .htw-card {
    padding: 2.5rem 2rem;
  }
}
</style>
